import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FiMenu, FiX, FiSettings } from 'react-icons/fi';
import LanguageSwitcher from './LanguageSwitcher';
import useLanguage from '../../hooks/useLanguage';
import navigationData from '../../data/navigation.json';

/**
 * Header Component with Navigation
 * - Desktop: Horizontal navigation bar
 * - Mobile: Hamburger menu
 * - Includes language switcher
 */
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language } = useLanguage();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="text-2xl md:text-3xl font-bold text-primary">
              RadarTech
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigationData.menuItems.map((item) => (
              <NavLink
                key={item.id}
                to={item.path}
                className={({ isActive }) =>
                  `text-base font-medium transition-colors duration-200 ${
                    isActive
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-gray-700 hover:text-primary'
                  }`
                }
              >
                {language === 'ko' ? item.label : item.labelEn}
              </NavLink>
            ))}

            {/* Admin Link - Styled Differently */}
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `flex items-center space-x-1 text-sm font-medium px-3 py-1.5 rounded-md transition-all duration-200 ${
                  isActive
                    ? 'bg-gray-700 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-700 hover:text-white border border-gray-300'
                }`
              }
            >
              <FiSettings size={16} />
              <span>{language === 'ko' ? '관리자' : 'Admin'}</span>
            </NavLink>
          </nav>

          {/* Language Switcher - Desktop */}
          <div className="hidden md:flex items-center">
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-primary hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={closeMobileMenu}
          ></div>

          {/* Menu Panel */}
          <div className="fixed top-16 left-0 right-0 bg-white shadow-lg z-50 md:hidden">
            <nav className="flex flex-col p-4 space-y-4">
              {navigationData.menuItems.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={({ isActive }) =>
                    `text-lg font-medium py-2 px-4 rounded-md transition-colors duration-200 ${
                      isActive
                        ? 'text-primary bg-blue-50'
                        : 'text-gray-700 hover:text-primary hover:bg-gray-100'
                    }`
                  }
                >
                  {language === 'ko' ? item.label : item.labelEn}
                </NavLink>
              ))}

              {/* Admin Link - Mobile */}
              <NavLink
                to="/admin"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `flex items-center space-x-2 text-base font-medium py-2 px-4 rounded-md transition-all duration-200 ${
                    isActive
                      ? 'bg-gray-700 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-700 hover:text-white border border-gray-300'
                  }`
                }
              >
                <FiSettings size={18} />
                <span>{language === 'ko' ? '관리자' : 'Admin'}</span>
              </NavLink>

              {/* Language Switcher - Mobile */}
              <div className="border-t pt-4">
                <LanguageSwitcher />
              </div>
            </nav>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
